import Product from 'containers/Product';
import PropTypes from 'prop-types';
import globalProps from 'utils/globalProps';
import {
  MEDIA_IMAGE_DETAILS_QUERY,
  PRODUCT_BY_HANDLE,
  productByHandleVars,
} from 'helpers/graphql/product';
import client from 'lib/shopify-storefront/client';
import Error from 'components/Error';
import Loader from 'components/Loader';
import { useRouter } from 'next/router';
import { productView } from 'lib/events/fb-pixel/product-view';
import { productViewTwitter } from 'lib/events/tw-pixel/product-view';
import { useEffect, createContext, useState, lazy, Suspense } from 'react';
import Head from 'next/head';

import ContainedHead from 'components/ContainedHead';
import { fixProductDataMetafields } from 'helpers/fixPriceObjectModel';
import Prismic from 'helpers/Prismic';
import Script from 'next/script';
import TiktokPixel from 'tiktok-pixel';
import { v4 as uuidv4 } from 'uuid';

const ProductAccessories = lazy(() =>
  import('containers/Product/product-accessories'),
);
const ProductClearance = lazy(() =>
  import('containers/Product/product-clearance'),
);
const ProductRTS = lazy(() => import('containers/Product/product-rts'));

const P = ({
  productData,
  global,
  accessory_list,
  disclaimer,
  imagesData,
  recommendedBuilds,
}) => {
  const { isFallback, asPath } = useRouter();

  const eventId = uuidv4();

  const customFieldsFilter =
    productData?.product?.metafields?.length > 0 &&
    productData?.product?.metafields?.filter(
      (el) => el?.namespace?.toLowerCase() === 'custom',
    );
  const shortDescription = customFieldsFilter
    ? customFieldsFilter.find((item) => item?.key === 'short_description')
        ?.value ?? ''
    : '';

  const schema = {
    '@context': 'http://schema.org/',
    '@type': productData?.product?.__typename,
    name: productData?.product?.title,
    url: `https://www.aftershockpc.com.au/products/${productData?.product?.handle}`,
    image: [productData?.product?.images?.edges[0]?.node?.transformedSrc],
    description: shortDescription,
    sku: productData?.product?.variants?.edges[0]?.node?.sku,
    brand: {
      '@type': 'Brand',
      name: 'Aftershock',
    },
    offers: [
      {
        '@type': 'Offer',
        sku: productData?.product?.variants?.edges[0]?.node?.sku,
        availability: 'http://schema.org/InStock',
        price: productData?.product?.priceRange?.minVariantPrice?.amount,
        priceCurrency: 'AUD',
        url: `https://www.aftershockpc.com.au/products/${productData?.product?.handle}`,
      },
    ],
  };

  // Get accessories feature images
  const featureImages = {
    desktop:
      imagesData?.product?.featureImageDesktop?.reference?.image?.originalSrc,
    mobile:
      imagesData?.product?.featureImageMobile?.reference?.image?.originalSrc,
  };

  if (isFallback) {
    return <Loader />;
  }
  if (
    productData === null ||
    productData.product === null ||
    productData?.product.tags.includes('custom')
  ) {
    return <Error code={404} />;
  }

  useEffect(() => {
    const currentProduct = productData?.product;
    TiktokPixel.init(process.env.NEXT_PUBLIC_TIKTOK_PIXEL_ID);
    TiktokPixel.track('ViewContent', {
      content_category: 'product',
      content_id: (currentProduct?.id).replace('gid://shopify/Product/', ''),
      content_name: currentProduct?.title,
      content_type: 'product',
      currency: 'AUD',
      value: currentProduct?.priceRange.minVariantPrice.amount,
      event_id: eventId,
    });
    const interval = setInterval(() => {
      if (window.fbq) {
        clearInterval(interval);

        productView(productData?.product);
        productViewTwitter(productData?.product);
        const dataLayerProductData = {
          event: 'view_content',
          content_type: 'product',
          quantity: 1,
          description: currentProduct?.description,
          content_id: (currentProduct?.id).replace(
            'gid://shopify/Product/',
            '',
          ),
          content_name: currentProduct?.title,
          currency: 'AUD',
          value: currentProduct?.priceRange.minVariantPrice.amount,
          price: currentProduct?.priceRange.minVariantPrice.amount,
          content_category: 'computer',
        };
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(dataLayerProductData);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('giftcard', 0);
  }, []);

  const getComponentToRender = () => {
    const productType = productData?.product?.productType
      ?.replace(/ /g, '')
      ?.replace(/-/g, '')
      ?.toLowerCase();
    const tags =
      productData?.product?.tags?.map((item) => item.toLowerCase()) || [];

    if (productType === 'readytoship') {
      return (
        <ProductRTS
          data={fixProductDataMetafields(productData)}
          globalData={global.global_data}
          disclaimer={disclaimer}
        />
      );
    }
    if (tags.includes('clearance')) {
      return <ProductClearance data={productData} />;
    }
    if (productType === 'accessories') {
      return (
        <GiftCardContextProvider>
          <ProductAccessories
            data={fixProductDataMetafields(productData)}
            accessory_list={accessory_list}
            featureImages={featureImages}
            recommendedBuilds={recommendedBuilds}
          />
        </GiftCardContextProvider>
      );
    }
    return (
      <GiftCardContextProvider>
        <ProductAccessories
          data={fixProductDataMetafields(productData)}
          accessory_list={accessory_list}
          featureImages={featureImages}
          recommendedBuilds={recommendedBuilds}
        />
      </GiftCardContextProvider>
    );
  };

  return (
    <>
      <Head>
        <link
          rel="preconnect dns-prefetch"
          href="https://triplewhale-pixel.web.app/"
          crossOrigin="true"
        />
        <link
          rel="preconnect dns-prefetch"
          href="https://api.config-security.com/"
          crossOrigin="true"
        />
        <meta
          property="og:image"
          content={productData.product.images.edges[0].node.transformedSrc}
        />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="1024" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      </Head>
      <Script
        src="/tripleWhale.js"
        dangerouslySetInnerHTML={{
          __html: 'TripleHeadless = "aftershockpcau.myshopify.com" ',
        }}
        strategy="lazyOnload"
      />
      <Script
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_ID}');`,
        }}
        strategy="lazyOnload"
      />
      <ContainedHead
        title={
          productData?.product?.seo?.title != null
            ? productData?.product?.seo?.title
            : productData?.product?.title
        }
        description={
          productData?.product?.seo?.description ||
          productData?.product?.description
        }
        path={asPath}
      />
      <Suspense fallback={<Loader />}>{getComponentToRender()}</Suspense>
    </>
  );
};

P.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  productData: PropTypes.object,
  global: PropTypes.object,
  accessory_list: PropTypes.array,
  disclaimer: PropTypes.array,
  imagesData: PropTypes.object,
  recommendedBuilds: PropTypes.array,
};

export default P;

export async function getStaticProps({ params, preview = false }) {
  const {
    props: { global },
  } = await globalProps();
  const { handle } = params;

  const productHandle = handle?.join('-');
  const { data: productData } = await client.query({
    query: PRODUCT_BY_HANDLE,
    variables: productByHandleVars(productHandle),
  });

  const disclaimer = await Prismic.getByUID(
    'aux_page',
    'aux-panel-printing-disclaimer',
  );
  // console.log({ disclaimer: disclaimer.data.body })
  // const rtsData = await Prismic.getByUID('ready_to_ship_page', `rts-desktops`);
  // const RecomemdedBuilds = await Prismic.getByUID(
  //   'recommended_builds',
  //   'recommended_builds',
  // );
  // console.log("RecomemdedBuilds", RecomemdedBuilds.data.products)

  const productType = productData.product?.productType
    ?.replace(/ /g, '')
    ?.replace(/-/g, '')
    ?.toLowerCase();

  let recommendedBuilds = null;
  let accessory_list = null;
  let imagesData = null;
  let filteredRtsProducts = null;

  if (productType !== 'readytoship') {
    const { data: rtsData } = await Prismic.getByUID(
      'ready_to_ship_page',
      `rts-desktops`,
    );
    const rtsProducts = rtsData.body || null;

    filteredRtsProducts = rtsProducts?.filter(
      (product) =>
        product.id.includes('aftershock_product_list') &&
        (product.primary.product_title === '1080P GAMING' ||
          product.primary.product_title === '1440P' ||
          product.primary.product_title === '4K GAMING'),
    );

    let selected1440PModel = null;

    recommendedBuilds = filteredRtsProducts?.map((product) => {
      if (product.primary.product_title === '1080P GAMING') {
        return product.items.find(
          (item) =>
            Number(item.product?.variants[0]?.price) > 999 &&
            Number(item.product?.variants[0]?.price) < 2000 &&
            item.product?.variants[0].inventory_quantity > 0,
        );
      }
      if (product.primary.product_title === '1440P') {
        const foundProduct = product.items.find(
          (item) =>
            Number(item.product?.variants[0]?.price) > 1999 &&
            Number(item.product?.variants[0]?.price) < 3000 &&
            item.product?.variants[0].inventory_quantity > 0,
        );
        selected1440PModel = foundProduct?.product?.id; // Зберігаємо ID обраної моделі
        return foundProduct;
      }
      if (product.primary.product_title === '4K GAMING') {
        const productFound = product.items.find(
          (item) =>
            Number(item.product?.variants[0]?.price) > 2999 &&
            Number(item.product?.variants[0]?.price) < 10000 &&
            item.product?.variants[0].inventory_quantity > 0,
        );

        if (!productFound) {
          // Якщо немає 4K GAMING моделі, беремо найдорожчу доступну 1440P модель, що не збігається з обраною
          const fallbackProduct = filteredRtsProducts
            ?.find((p) => p.primary.product_title === '1440P')
            ?.items.filter(
              (item) =>
                item.product?.id !== selected1440PModel && // Виключаємо вже обрану модель
                item.product?.variants[0]?.inventory_quantity > 0,
            )
            .sort(
              (a, b) =>
                Number(b.product?.variants[0]?.price) -
                Number(a.product?.variants[0]?.price),
            )[0]; // Беремо найдорожчу модель
          return fallbackProduct || null;
        }

        return productFound;
      }
      return null;
    });

    recommendedBuilds = recommendedBuilds?.filter(Boolean);

    const content = await Prismic.getByUID(
      'collections_accessories',
      'cls-accessories',
    );
    const product_list = content.data.body.filter((el) => {
      if (el.slice_type === 'aftershock_accessories_product_list') return el;
    });

    accessory_list = product_list[0]?.items || null;

    const { data: imagesDataQuery } = await client.query({
      query: MEDIA_IMAGE_DETAILS_QUERY,
      variables: productByHandleVars(productHandle),
    });
    imagesData = imagesDataQuery || null;
  }

  return {
    props: {
      productData: productData || null,
      imagesData,
      global,
      accessory_list,
      disclaimer: disclaimer.data.body,
      recommendedBuilds,
    },
    revalidate: 3600,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  };
}

export const GiftCardContext = createContext();

export const GiftCardContextProvider = ({ children }) => {
  const [giftCard, setGiftCard] = useState();
  const [gcidx, setIdx] = useState();

  return (
    <GiftCardContext.Provider
      value={{
        giftCard,
        setGiftCard,
        gcidx,
        setIdx,
      }}
    >
      {children}
    </GiftCardContext.Provider>
  );
};
